/* eslint-disable import/no-cycle */
import * as signalR from "@microsoft/signalr";
import { HubConnectionState } from "@microsoft/signalr";
import { Profile } from "oidc-client";
import React, {
    createContext,
    Dispatch,
    SetStateAction,
    useContext,
    useEffect,
    useState,
} from "react";
import { Redirect, Route } from "react-router-dom";
import { Vector3 } from "three";
import MessageImage from "../../assets/images/message.png";
import { RenderModelType, RightPanelsModel } from "../Models/AssetInformationModels";
import { ChartsModel } from "../Models/SignalModels";
import { Roles, User } from "../Models/UserTable.model";
import {
    AzureUsersModel,
    FileParameter,
    GetAssetModel,
    GetAssetsForDbidContentModel,
    // GetAlarmsCountByGravityModel,
    IndexModel,
    ProjectDto,
    RenderModel2,
    UserNotificationDto,
} from "../api/APIClient";
import {
    fetchGetCountEquipos,
    fetchPermitsToWork,
    fetchPowerBIReports,
    getHeatmapData,
} from "../api/Fetchers";
import { IPowerBIReportElementAPI } from "../api/Interfaces";
import {
    alarmsService,
    chatService,
    notificationsService,
    projectsService,
    userInformationService,
    userPermitsService,
} from "../api/Services";
import { authService } from "../auth/AuthorizeService";
import { usePureEffect } from "../helpers/usePureEffect";
import { ProjectContext } from "./Project/Context/ProjectContext";
import {
    BrokenParts,
    renderTaskProgress,
    ViewerMode,
} from "./Project/Viewer/ForgeViewer/BIMDigitalTwinViewer/Utils";
import {
    HeatmapGroup,
    HeatmapBasicInfo,
    HeatmapId,
    Heatmap,
    HeatmapLegendGroup,
} from "./Project/Viewer/Heatmaps/HeatmapModels";
import { IMessagesBody } from "./Project/HoverChat/HoverChatModels";

type SetState<T> = Dispatch<SetStateAction<T>>;

const userPermitsConnection = new signalR.HubConnectionBuilder()
    .withUrl("/hubs/userPermits", {
        accessTokenFactory: async () => {
            const token = await authService.getAccessToken();
            return token !== null ? token : "";
        },
        withCredentials: true,
    })
    .configureLogging(signalR.LogLevel.Error)
    .withAutomaticReconnect()
    .build();

const providerConnection = new signalR.HubConnectionBuilder()
    .withUrl("/hubs/provider", {
        accessTokenFactory: async () => {
            const token = await authService.getAccessToken();
            return token !== null ? token : "";
        },
        withCredentials: true,
    })
    .configureLogging(signalR.LogLevel.Error)
    .withAutomaticReconnect()
    .build();

const heatmapConnection = new signalR.HubConnectionBuilder()
    .withUrl("/hubs/heatmaps", {
        accessTokenFactory: async () => {
            const token = await authService.getAccessToken();
            return token !== null ? token : "";
        },
        withCredentials: true,
    })
    .configureLogging(signalR.LogLevel.Error)
    .withAutomaticReconnect()
    .build();

interface IPanelViewerProviderProps {
    children: React.ReactNode;
}
export interface UserRole {
    userId: string;
    userName: string;
    roleId: string;
    roles: string;
    projectId: string;
    sections: ModulesInterface;
}
export interface ModulesInterface {
    sectionId: string;
    sectionName: string;
    modules: unknown;
}

interface IObjTest {
    xcoord: number;
    ycoord: number;
    data: ChartsModel;
}

interface IAlarmsHeatMap {
    dbIds: number[] | undefined;
    color: string | undefined;
}

interface GanttColumns {
    columnHeader: string;
    values: string;
}

let defaulValues;

export interface IPanelProjectContext {
    project: ProjectDto;
    setProject: SetState<ProjectDto>;

    notifications: UserNotificationDto[];
    setNotifications: SetState<UserNotificationDto[]>;
    notificationsCount: number;
    setNotificationsCount: SetState<number>;
    alarmsCount: number;
    setAlarmsCount: SetState<number>;

    redirectCommentsPanel: boolean;
    setRedirectCommentsPanel: SetState<boolean>;

    isViewerReady: boolean;
    setisViewerReady: SetState<boolean>;

    view: string;
    setView: SetState<string>;
    filterView: string;
    setFilterView: SetState<string>;

    hoveredDeviceInfo: IObjTest | undefined;
    setHoveredDeviceInfo: SetState<IObjTest | undefined>;

    rightPanelsData: RightPanelsModel | undefined;
    setRightPanelsData: SetState<RightPanelsModel | undefined>;

    isRightPanelOpen: boolean;
    setIsRightPanelOpen: SetState<boolean>;

    isViewerLoading: boolean;
    setIsViewerLoading: SetState<boolean>;

    isRenderingItems: boolean;
    setIsRenderingItems: SetState<boolean>;

    dbidsToRender: number[];
    setDbidsToRender: SetState<number[]>;

    assetSelection: IndexModel | undefined;
    setAssetSelection: SetState<IndexModel | undefined>;

    viewerMode: ViewerMode;
    setViewerMode: SetState<ViewerMode>;

    dbidsAlarmsHeatMap: IAlarmsHeatMap[];
    setDbidsAlarmsHeatMap: SetState<IAlarmsHeatMap[]>;

    renderModel: RenderModel2[] | undefined;
    setRenderModel: SetState<RenderModel2[] | undefined>;

    userProfile: Profile | null;
    setUserProfile: SetState<Profile | null>;

    userRole: UserRole | undefined; // no se usa en provider más allá de set/get; ajusta si tienes el tipo concreto
    setUserRole: SetState<UserRole | undefined>;

    userPermits: { [key: string]: number };
    setUserPermits: SetState<{ [key: string]: number }>;

    usersFromAzure: AzureUsersModel[];
    setUsersFromAzure: SetState<AzureUsersModel[]>;

    // Admin / tablas
    cellValues: User[];
    setCellValues: SetState<User[]>;
    users: User[];
    setUsers: SetState<User[]>;

    userName: string;
    setUserName: SetState<string>;

    cellValuesRoles: Roles[];
    setCellValuesRoles: SetState<Roles[]>;
    roles: Roles[];
    setRoles: SetState<Roles[]>;
    userRoles: string[];
    setUserRoles: React.Dispatch<React.SetStateAction<string[]>>;

    workOrdersOpen: string[];
    setWorkOrdersOpen: SetState<string[]>;

    editing: boolean;
    setEditing: SetState<boolean>;
    changePermitsTables: boolean;
    setChangePermitsTables: SetState<boolean>;
    submitEditChanges: boolean;
    setSubmitEditChanges: SetState<boolean>;
    submitEditChangesRoles: boolean;
    setSubmitEditChangesRoles: SetState<boolean>;

    tableFrom: string;
    setTableFrom: SetState<string>;
    showSave: boolean;
    setShowSave: SetState<boolean>;

    // Asset edit
    assetType: string | undefined;
    setAssetType: SetState<string | undefined>;
    assetEditModel: GetAssetModel | undefined;
    setAssetEditModel: SetState<GetAssetModel | undefined>;
    parent: string | undefined;
    setParent: SetState<string | undefined>;
    dbIdsToEdit: number[] | undefined;
    setDbIdsToEdit: SetState<number[] | undefined>;
    assetIdEdit: string | undefined;
    setAssetIdEdit: SetState<string | undefined>;
    openEditAsset: boolean;
    setOpenEditAsset: SetState<boolean>;
    assetsWithSameDbid: GetAssetsForDbidContentModel[] | undefined;
    setAssetsWithSameDbid: SetState<GetAssetsForDbidContentModel[] | undefined>;

    // Usuario / perfil
    userId: string | undefined;
    setUserId: React.Dispatch<React.SetStateAction<string>>;
    userProfileId: string | undefined;
    userProfilePhoto: string | undefined;

    // Render types
    renderModelTypes: RenderModelType[];
    setRenderModelTypes: SetState<RenderModelType[]>;

    // PowerBI
    powerBIFilteredData: IPowerBIReportElementAPI[];
    setPowerBIFilteredData: SetState<IPowerBIReportElementAPI[]>;
    powerBILegend: string[];
    setPowerBILegend: SetState<string[]>;
    powerBIFilter: string;
    setPowerBIFilter: SetState<string>;

    // Diálogos
    openDialogEditUser: string;
    setOpenDialogEditUser: SetState<string>;
    openDialogRole: string;
    setOpenDialogRole: SetState<string>;

    // Métricas / contadores
    projectsCount: number;
    setProjectsCount: SetState<number>;
    documentsCount: number;
    setDocumentsCount: SetState<number>;
    teamsCount: number;
    setTeamsCount: SetState<number>;
    workOrdersCount: number | undefined;
    setWorkOrdersCount: SetState<number | undefined>;
    usersWithAccessCount: number;
    setUsersWithAccessCount: SetState<number>;
    powerBIReportsCount: number;
    setPowerBIReportsCount: SetState<number>;
    chatSubscriptionsCount: number;
    setChatSubscriptionsCount: SetState<number>;
    signalSubscriptionsCount: number;
    setSignalSubscriptionsCount: SetState<number>;

    rightPanelButtonsLoading: boolean;
    setRightPanelButtonsLoading: SetState<boolean>;

    interferenceOrdersOpen: string[];
    setInterferenceOrdersOpen: SetState<string[]>;

    // Auth helpers
    isUserAllowedById: (id: string) => boolean;
    createRouteBasedOnAuth: (
        isAuthorized: boolean,
        authPath: string,
        AuthComponent: React.ComponentType,
        routeToRedirect: string
    ) => JSX.Element;

    // Permisos: estado carga
    isUserPermitsLoading: boolean;
    errorInUserPemirts: boolean;

    // Más diálogos
    openDialogUsersAssociates: string;
    setOpenDialogUsersAssociates: SetState<string>;
    showDialogEditRole: string;
    setShowDialogEditRole: SetState<string>;
    showDeleteDialog: string;
    setShowDeleteDialog: SetState<string>;

    // Gantt / tablas externas
    foreingCellsValues: GanttColumns[];
    setForeingCellsValues: SetState<GanttColumns[]>;
    foreingHeaderValues: { columnHeader: string; values: string } | undefined;
    setForeingHeaderValues: SetState<{ columnHeader: string; values: string } | undefined>;

    // Coordenadas / viewer
    clickCoords: Vector3 | undefined;
    coordinatesMode: boolean;
    setCoordinatesMode: SetState<boolean>;

    floorPlanes: THREE.Vector4[]; // si usas THREE.Vector4 global, cámbialo por ese tipo
    setFloorPlanes: SetState<THREE.Vector4[]>;

    isolatedAssets: number[];
    setIsolatedAssets: SetState<number[]>;

    areaVisualization: { show: boolean; level: string; area: string; roof: boolean };
    setAreaVisualization: SetState<{ show: boolean; level: string; area: string; roof: boolean }>;

    validateLevelAccess: (moduleName: string, requiredLevelAccess: number) => boolean;

    heatmapLegend: HeatmapLegendGroup | null;
    setHeatmapLegend: SetState<HeatmapLegendGroup | null>;
    heatmapList: HeatmapBasicInfo[];
    setHeatmapList: SetState<HeatmapBasicInfo[]>;
    heatmapSelected: HeatmapId | null;
    setHeatmapSelected: SetState<HeatmapId | null>;
    heatmap: HeatmapGroup | null;
    setHeatmap: SetState<HeatmapGroup | null>;
    selectedSensorId: string | null;
    setSelectedSensorId: SetState<string | null>;
    isHeatmapListLoading: boolean;
    setIsHeatmapListLoading: SetState<boolean>;
    isHeatmapLoading: boolean;
    setIsHeatmapLoading: SetState<boolean>;
    createHeatmap: (heatmap, selectedSensorId) => Promise<void>;
    showHMTooltips: boolean;
    setShowHMTooltips: SetState<boolean>;
    isolateHMAssets: boolean;
    setIsolateHMAssets: SetState<boolean>;
    heatmapSignalRState: boolean;
    setHeatmapSignalRState: SetState<boolean>;
    cleanHeatmapData: () => void;
    requestedDatetime: Date | null;
    setRequestedDatetime: SetState<Date | null>;
    redirectToChatThread: string | undefined;
    setRedirectToChatThread: SetState<string | undefined>;
    documentsTag: string | null;
    setDocumentsTag: SetState<string | null>;
    messages: IMessagesBody[];
    setMessages: Dispatch<SetStateAction<IMessagesBody[]>>;
    imageCache: { [key: string]: FileParameter };
    setImageCache: Dispatch<SetStateAction<{ [key: string]: FileParameter }>>;
}

const PanelProjectContext = createContext<IPanelProjectContext | undefined>(undefined);

interface ISprite2D {
    xcoord: number;
    ycoord: number;
    id: number;
    clicked?: boolean;
}

const PanelProjectProvider = ({ children }: IPanelViewerProviderProps): JSX.Element => {
    const [project, setProject] = useState<ProjectDto>(
        React.useContext<ProjectDto>(ProjectContext)
    );

    const [notifications, setNotifications] = useState<UserNotificationDto[]>([]);
    const [userProfile, setUserProfile] = useState<Profile | null>(null);
    const [userRole, setUserRole] = useState<UserRole>();
    const [userRoles, setUserRoles] = useState<string[]>([]);
    const [userPermits, setUserPermits] = useState<{ [key: string]: number }>({});
    const [isUserPermitsLoading, setIsUserPermitsLoading] = useState<boolean>(true);
    const [errorInUserPemirts, setErrorInUserPemirts] = useState<boolean>(false);
    const [redirectCommentsPanel, setRedirectCommentsPanel] = useState<boolean>(false);
    const [isViewerReady, setisViewerReady] = useState<boolean>(false);
    const [view, setView] = React.useState("");
    const [filterView, setFilterView] = React.useState("");
    const [hoveredDeviceInfo, setHoveredDeviceInfo] = useState<IObjTest | undefined>(undefined);
    const [rightPanelsData, setRightPanelsData] = useState<RightPanelsModel | undefined>({});
    const [isRightPanelOpen, setIsRightPanelOpen] = useState<boolean>(false);
    const [isViewerLoading, setIsViewerLoading] = useState<boolean>(false);
    const [isRenderingItems, setIsRenderingItems] = useState<boolean>(false);
    const [assetSelection, setAssetSelection] = useState<IndexModel | undefined>({});
    const [viewerMode, setViewerMode] = useState<ViewerMode>(ViewerMode.AssetInfo);
    const [dbidsToRender, setDbidsToRender] = useState<number[]>([]);
    const [renderModel, setRenderModel] = useState<RenderModel2[] | undefined>(undefined);
    const [dbidsAlarmsHeatMap, setDbidsAlarmsHeatMap] = useState<IAlarmsHeatMap[]>([]);
    const [userId, setUserId] = useState<string>("");
    const [usersFromAzure, setUsersFromAzure] = useState<AzureUsersModel[]>([]);
    // Admin management
    const [editing, setEditing] = React.useState(false);
    const [changePermitsTables, setChangePermitsTables] = React.useState(true);
    const [submitEditChanges, setSubmitEditChanges] = React.useState(false);
    const [tableFrom, setTableFrom] = React.useState<string>("");
    const [showSave, setShowSave] = React.useState<boolean>(false);
    const [openDialogRole, setOpenDialogRole] = useState("");
    const [openDialogEditUser, setOpenDialogEditUser] = useState("");
    const [showDialogEditRole, setShowDialogEditRole] = React.useState<string>("");
    const [showDeleteDialog, setShowDeleteDialog] = useState("");
    const [openDialogUsersAssociates, setOpenDialogUsersAssociates] = useState<string>("");
    const [floorPlanes, setFloorPlanes] = React.useState<THREE.Vector4[]>([]);
    const [isolatedAssets, setIsolatedAssets] = useState<number[]>([]);
    const [areaVisualization, setAreaVisualization] = useState({
        show: false,
        level: "",
        area: "",
        roof: false,
    });
    const [documentsTag, setDocumentsTag] = useState<string | null>(null);

    // Users table
    const [cellValues, setCellValues] = React.useState<User[]>([]);
    const [users, setUsers] = React.useState<User[]>([]);
    // Roles table
    const [cellValuesRoles, setCellValuesRoles] = React.useState<Roles[]>([]);
    const [roles, setRoles] = React.useState<Roles[]>([]);
    // Roles By Users Table
    const [submitEditChangesRoles, setSubmitEditChangesRoles] = React.useState(false);
    const [userProfileId, setUserProfileId] = useState<string>();
    const [userProfilePhoto, setUserProfilePhoto] = useState<string | undefined>(undefined);

    const [assetType, setAssetType] = useState<string | undefined>(undefined);
    const [assetEditModel, setAssetEditModel] = useState<GetAssetModel | undefined>(undefined);
    const [parent, setParent] = React.useState<string | undefined>(undefined);
    const [dbIdsToEdit, setDbIdsToEdit] = useState<number[] | undefined>(undefined);
    const [assetIdEdit, setAssetIdEdit] = useState<string | undefined>(undefined);
    const [openEditAsset, setOpenEditAsset] = React.useState(false);
    const [assetsWithSameDbid, setAssetsWithSameDbid] = useState<
        GetAssetsForDbidContentModel[] | undefined
    >(undefined);
    const [renderModelTypes, setRenderModelTypes] = useState<RenderModelType[]>([]);
    const [powerBIFilteredData, setPowerBIFilteredData] = useState<IPowerBIReportElementAPI[]>([]);
    const [powerBILegend, setPowerBILegend] = useState<string[]>([]);
    const [powerBIFilter, setPowerBIFilter] = useState<string>("");

    const [projectsCount, setProjectsCount] = useState<number>(0);
    const [documentsCount, setDocumentsCount] = React.useState<number>(0);
    const [teamsCount, setTeamsCount] = React.useState<number>(0);
    const [workOrdersCount, setWorkOrdersCount] = React.useState<number | undefined>(0);
    const [usersWithAccessCount, setUsersWithAccessCount] = React.useState<number>(0);
    const [powerBIReportsCount, setPowerBIReportsCount] = React.useState<number>(0);
    const [chatSubscriptionsCount, setChatSubscriptionsCount] = React.useState<number>(0);
    const [signalSubscriptionsCount, setSignalSubscriptionsCount] = React.useState<number>(0);
    const [notificationsCount, setNotificationsCount] = React.useState<number>(0);
    const [alarmsCount, setAlarmsCount] = React.useState<number>(0);
    const [workOrdersOpen, setWorkOrdersOpen] = React.useState<string[]>([]);
    const [interferenceOrdersOpen, setInterferenceOrdersOpen] = React.useState<string[]>([]);

    const [rightPanelButtonsLoading, setRightPanelButtonsLoading] = useState<boolean>(false);
    // gantt viewer
    const [foreingCellsValues, setForeingCellsValues] = useState<GanttColumns[]>([]);
    const [foreingHeaderValues, setForeingHeaderValues] =
        useState<{ columnHeader: string; values: string }>();
    const [clickCoords, setClickCoords] = useState<Vector3 | undefined>(undefined);
    const [coordinatesMode, setCoordinatesMode] = useState(false);

    const [heatmapLegend, setHeatmapLegend] = useState<HeatmapLegendGroup | null>(null);
    const [heatmapList, setHeatmapList] = useState<HeatmapBasicInfo[]>([]);
    const [heatmapSelected, setHeatmapSelected] = useState<HeatmapId | null>(null);
    const [heatmap, setHeatmap] = useState<HeatmapGroup | null>(null);
    const [selectedSensorId, setSelectedSensorId] = useState<string | null>(null);
    const [isHeatmapListLoading, setIsHeatmapListLoading] = useState<boolean>(false);
    const [isHeatmapLoading, setIsHeatmapLoading] = useState<boolean>(false);
    const [showHMTooltips, setShowHMTooltips] = useState<boolean>(true);
    const [isolateHMAssets, setIsolateHMAssets] = useState<boolean>(true);
    const [heatmapSignalRState, setHeatmapSignalRState] = useState<boolean>(true);
    const [requestedDatetime, setRequestedDatetime] = useState<Date | null>(null);

    const [userName, setUserName] = useState<string>("");
    const [messages, setMessages] = React.useState<IMessagesBody[]>([]);
    const [imageCache, setImageCache] = React.useState<{ [key: string]: FileParameter }>({});

    const cleanHeatmapData = () => {
        setHeatmapSelected(null);
        setSelectedSensorId(null);
        setHeatmapLegend(null);
        setRequestedDatetime(null);
    };

    const [redirectToChatThread, setRedirectToChatThread] = useState<string>();

    const getSensorValue = (surfaceShadingPoint, sensorType) => {
        return surfaceShadingPoint.contextData.value;
    };

    const createHeatmap = async (hm: HeatmapGroup, selectedHMSensorId: string) => {
        if (!hm || !selectedHMSensorId) {
            return;
        }

        // Check if DataVisualization extension is available
        if (!window.NOP_VIEWER) {
            console.error("Viewer not available");
            return;
        }

        const dataVizExtn = window.NOP_VIEWER.getExtension(
            "Autodesk.DataVisualization"
        ) as Autodesk.Extensions.DataVisualization;

        if (!dataVizExtn) {
            console.error("DataVisualization extension not loaded");
            return;
        }

        const selectedHeatmapSensor = hm.heatmaps.find(
            (sensor) => sensor.typeId === selectedHMSensorId
        );
        if (!selectedHeatmapSensor) {
            return;
        }

        // Normalize values in the selected heatmap sensor
        const normalizedHeatmapSensor: Heatmap = {
            ...selectedHeatmapSensor,
            nodes: selectedHeatmapSensor.nodes?.map((node) => {
                // Check if any point in this node needs floor projection
                const hasFloorProjection = node.points?.some((point) => point.projectTheFloor);
                const hasPointByCoord = node.points?.some((point) => point.coord);
                let updatedNodeDbIds = node.dbIds;

                if ((hasFloorProjection || hasPointByCoord) && window.NOP_VIEWER) {
                    // Get all projected dbIds from points that need floor projection
                    const projectedDbIds: number[] = [];

                    node.points?.forEach((point) => {
                        if (
                            point.projectTheFloor &&
                            Array.isArray(point.dbIds) &&
                            point.dbIds.length > 0
                        ) {
                            // Get the projected floor center coordinates
                            const projectedCoords = window.NOP_VIEWER.getDbidProyectedFloorCenter(
                                point.dbIds
                            );

                            // Get the dbId of the asset at the projected position
                            const projectedDbId =
                                window.NOP_VIEWER.getDbIdAtPosition(projectedCoords);

                            // If we found an asset at the projected position, add it to the array
                            if (projectedDbId && !projectedDbIds.includes(projectedDbId)) {
                                projectedDbIds.push(projectedDbId);
                            }
                        } else if (point.coord) {
                            const projectedDbId = window.NOP_VIEWER.getDbIdAtPosition(point.coord);
                            if (projectedDbId && !projectedDbIds.includes(projectedDbId)) {
                                projectedDbIds.push(projectedDbId);
                            }
                        }
                    });

                    // Add projected dbIds to the node's dbIds array
                    updatedNodeDbIds = [...node.dbIds, ...projectedDbIds];
                }

                return {
                    ...node,
                    dbIds: updatedNodeDbIds,
                    points: node.points?.map((point) => {
                        const { min, max } = selectedHeatmapSensor.legend;
                        const normalizedValue = (point.value - min) / (max - min);
                        const clampedValue = Math.max(0, Math.min(1, normalizedValue));

                        return {
                            ...point,
                            value: clampedValue,
                        };
                    }),
                };
            }),
        };

        // Create a modified heatmap group with normalized values
        const normalizedHeatmapGroup: HeatmapGroup = {
            ...hm,
            heatmaps: [normalizedHeatmapSensor],
        };

        try {
            await window.NOP_VIEWER.loadHeatmap(normalizedHeatmapGroup, selectedHMSensorId);
            dataVizExtn.renderSurfaceShading(hm.id, selectedHeatmapSensor.typeId, getSensorValue, {
                heatmapConfig: {
                    confidence: selectedHeatmapSensor.confidence,
                },
            });
            if (normalizedHeatmapSensor.legend) {
                setHeatmapLegend({
                    name: normalizedHeatmapSensor.legend.name,
                    legendItems: normalizedHeatmapSensor.legend.legendItems,
                });
            } else {
                setHeatmapLegend(null);
            }
            if (Array.isArray(hm.cuttingPlanes) && hm.cuttingPlanes.length > 0) {
                window.NOP_VIEWER.setCutPlanes(
                    hm.cuttingPlanes.map((cp) => new THREE.Vector4(cp.x, cp.y, cp.z, cp.w))
                );
            }
        } catch (error) {
            console.error("Error creating heatmap:", error);
        }
    };

    // Calculate timezone offset in hours relative to UTC (e.g., +1, -2)
    // JavaScript's getTimezoneOffset() returns minutes with inverted sign
    const getTimezoneOffset = (): number => {
        return -new Date().getTimezoneOffset() / 60;
    };

    const subscribeToHeatmapSignalR = () => {
        heatmapConnection.start().then(() => {
            heatmapConnection.invoke(
                "AddHeatMapConnection",
                project.id,
                heatmapSelected,
                getTimezoneOffset()
            );
            heatmapConnection.on("PushHeatMaps", (heatmapData: HeatmapGroup) => {
                setHeatmap(heatmapData);
            });
        });
    };

    const connectHeatmapSignalR = () => {
        if (heatmapConnection.state === HubConnectionState.Connected) {
            heatmapConnection.stop().then(() => {
                subscribeToHeatmapSignalR();
            });
        } else {
            subscribeToHeatmapSignalR();
        }
    };

    const disconnectHeatmapSignalR = () => {
        if (heatmapConnection.state === HubConnectionState.Connected) {
            heatmapConnection.stop();
        }
    };

    useEffect(() => {
        if (heatmapSelected === null) {
            disconnectHeatmapSignalR();
            setHeatmap(null);
            setRequestedDatetime(null);
        } else {
            // Initialize requestedDatetime when selecting a heatmap
            if (!requestedDatetime) {
                setRequestedDatetime(new Date());
            }
            // Use requestedDatetime if available, otherwise use current date
            // Backend expects UTC format with Z (ISO 8601): "2024-01-15T14:30:00.000Z"
            const dateToUse = requestedDatetime?.toISOString() || new Date().toISOString();
            getHeatmapData({
                projectId: project.id,
                userId,
                heatmapId: heatmapSelected,
                date: dateToUse,
                timezoneOffset: getTimezoneOffset(),
            })
                .then((res) => {
                    if (res.statusCode === 200 && res.data !== null) {
                        setHeatmap(res.data);
                        if (heatmapSignalRState) {
                            connectHeatmapSignalR();
                        } else {
                            disconnectHeatmapSignalR();
                        }
                    } else {
                        console.error(
                            "Error fetching heatmap:",
                            res.statusCode,
                            res.errorDescription
                        );
                        setHeatmap(null);
                    }
                })
                .catch((err) => {
                    console.error("Error fetching heatmap:", err);
                    setHeatmap(null);
                });
        }
    }, [heatmapSelected]);

    // Fetch heatmap when requestedDatetime changes (only if not in SignalR mode)
    useEffect(() => {
        if (heatmapSelected && !heatmapSignalRState && requestedDatetime) {
            // Backend expects UTC format with Z (ISO 8601): "2024-01-15T14:30:00.000Z"
            getHeatmapData({
                projectId: project.id,
                userId,
                heatmapId: heatmapSelected,
                date: requestedDatetime.toISOString(),
                timezoneOffset: getTimezoneOffset(),
            })
                .then((res) => {
                    if (res.statusCode === 200 && res.data) {
                        setHeatmap(res.data);
                        disconnectHeatmapSignalR();
                    } else {
                        console.error(
                            "Error fetching heatmap with datetime:",
                            res.statusCode,
                            res.errorDescription
                        );
                        setHeatmap(null);
                    }
                })
                .catch((err) => {
                    console.error("Error fetching heatmap with datetime:", err);
                    setHeatmap(null);
                });
        }
    }, [requestedDatetime]);

    useEffect(() => {
        if (heatmapSelected != null && heatmapSignalRState) {
            getHeatmapData({
                projectId: project.id,
                userId,
                heatmapId: heatmapSelected,
                date: new Date().toISOString(),
                timezoneOffset: getTimezoneOffset(),
            })
                .then((res) => {
                    if (res.statusCode === 200 && res.data) {
                        setHeatmap(res.data);
                        connectHeatmapSignalR();
                    } else {
                        console.error(
                            "Error fetching heatmap:",
                            res.statusCode,
                            res.errorDescription
                        );
                        setHeatmap(null);
                    }
                })
                .catch((err) => {
                    console.error("Error fetching heatmap:", err);
                    setHeatmap(null);
                });
        } else {
            disconnectHeatmapSignalR();
        }
    }, [heatmapSignalRState]);

    const UserPermitsCall = () => {
        if (project.id && userId) {
            userPermitsService
                .getUserPermits({ projectId: project.id })
                .then((response) => {
                    setUserPermits(response.data);
                    setErrorInUserPemirts(false);
                })
                .catch((err) => {
                    console.error("ERRORInuser", err);
                    setErrorInUserPemirts(true);
                })
                .finally(() => {
                    setIsUserPermitsLoading(false);
                });
        }
    };

    const validateLevelAccess = (moduleName: string, requiredLevelAccess: number): boolean => {
        return true;

        // if (userPermits[moduleName] && userPermits[moduleName] >= requiredLevelAccess) return true;
        // return false;
    };

    const isUserAllowedById = (id: string): boolean => {
        if (userId === id) {
            return true;
        }
        return false;
    };
    const createRouteBasedOnAuth = (
        isAuthorized: boolean,
        authPath: string,
        AuthComponent: React.ComponentType,
        routeToRedirect: string
    ) => {
        return (
            <Route
                path={authPath}
                render={() => {
                    return isAuthorized ? (
                        <AuthComponent />
                    ) : (
                        <>
                            <Redirect to={routeToRedirect} />
                        </>
                    );
                }}
            />
        );
    };

    const subscribeToUserPermitsSignalR = async () => {
        authService.getProfile().then((profResponse) => {
            if (profResponse !== null) {
                userPermitsConnection.start().then(() => {
                    authService.getProfile().then((prof) => {
                        if (prof !== null) {
                            userPermitsConnection.invoke("AddUserPermitsConnection", prof.oid);
                            userPermitsConnection.on("PushPermitsUpdated", () => {
                                UserPermitsCall();
                            });
                        }
                    });
                });
            }
        });
    };

    const subscribeToProviderSignalR = async () => {
        authService.getProfile().then((profResponse) => {
            if (profResponse !== null) {
                providerConnection.start().then(() => {
                    authService.getProfile().then((prof) => {
                        if (prof !== null) {
                            providerConnection.invoke("AddConnection", prof.oid);
                            providerConnection.on("OpenChatPannel", () => {
                                // if (!isViewerReady)
                                //     history.push(
                                //         BDTRoutes.Project.Viewer.Viewer.Builder(project.id)
                                //     );

                                // setRightPanelsData(undefined);
                                // setIsViewerLoading(true);
                                // setIsRightPanelOpen(false);
                                // setDbidsToRender([]);

                                renderTaskProgress(BrokenParts);
                                setAreaVisualization({
                                    show: true,
                                    level: "4",
                                    area: "west",
                                    roof: true,
                                });
                                window.NOP_VIEWER.addCoordLabelMakrup(
                                    {
                                        x: -403.24,
                                        y: -522.17,
                                        z: 51.15,
                                    },
                                    "Reported Issue Warning",
                                    MessageImage,
                                    "#7E26B6",
                                    true,
                                    () => {
                                        setRightPanelsData({
                                            confirmations: null,
                                            documents: null,
                                            info: null,
                                            isometrics: null,
                                            chat: {
                                                title: "Chat",
                                                description: "Chat",
                                                icon: "chat",
                                                data: {
                                                    threads: [
                                                        {
                                                            threadId: "mockedThread1",
                                                            title: "Reported Issue Warning",
                                                            isPublic: true,
                                                            createdAt: new Date(Date.now()),
                                                            createdBy:
                                                                "d67debce-7896-48d0-9a8f-c5d508ae6476",
                                                            sharedWith: [],
                                                            messages: [
                                                                {
                                                                    messageId: "mockedMessage1",
                                                                    isFile: true,
                                                                    message: undefined,
                                                                    fileName: "New image.png",
                                                                    fileType: "image/png",
                                                                    createdAt: new Date(Date.now()),
                                                                    createdBy:
                                                                        "d67debce-7896-48d0-9a8f-c5d508ae6476",
                                                                    readBy: [],
                                                                },
                                                                {
                                                                    messageId: "mockedMessage2",
                                                                    isFile: false,
                                                                    message:
                                                                        "Found it during inspection with contractor",
                                                                    fileName: undefined,
                                                                    fileType: undefined,
                                                                    createdAt: new Date(Date.now()),
                                                                    createdBy:
                                                                        "d67debce-7896-48d0-9a8f-c5d508ae6476",
                                                                    readBy: [],
                                                                },
                                                            ],
                                                        },
                                                    ],
                                                },
                                            },
                                        });

                                        setView("Reported Issue Warning");
                                    },
                                    true
                                );
                            });
                        }
                    });
                });
            }
        });
    };

    React.useEffect(() => {
        notificationsService.getUnreadNotifications(project.id).then((resp) => {
            if (resp) {
                setNotifications(resp.data);
            } else {
                setNotifications([]);
            }
        });
        notificationsService.getUnreadNotificationsCount(project.id).then((resp) => {
            if (resp) {
                setNotificationsCount(resp.data.number);
            } else {
                setNotificationsCount(0);
            }
        });
        projectsService.getAllProjectsCount().then((res) => {
            setProjectsCount(res.data.number ?? 0);
        });

        if (project.id !== undefined) {
            chatService.getThreadSubscriptionsByUser({ projectId: project.id }).then((res) => {
                setChatSubscriptionsCount(res.data.threadsId?.length ?? 0);
            });
            fetchPowerBIReports(project.id).then((res) => {
                setPowerBIReportsCount(res.data.length);
            });
            fetchPermitsToWork(project.id, undefined).then((res) => {
                setWorkOrdersCount(res.data.permitsToWork?.permitsToWork?.length);
            });
            fetchGetCountEquipos({ projectId: project.id }).then((res) => {
                setTeamsCount(res.data.count);
            });
        }

        authService.getProfile().then((resp) => {
            if (resp) {
                setUserId(resp.oid);
            }
        });

        setViewerMode(ViewerMode.Default);
    }, [project]);

    const handleCoordinatesClick = React.useCallback((ev) => {
        const result = window.NOP_VIEWER.clientToWorld(ev.offsetX, ev.offsetY);
        if (result && result.point) {
            setClickCoords(result.point);
            window.NOP_VIEWER.deleteAllSimpleVolumes();
            window.NOP_VIEWER.addSimpleSphere(
                {
                    volume: {
                        shape: "sphere",
                        height: 0,
                        radius: 1,
                    },
                    urn: "",
                    center: {
                        x: result.point.x,
                        y: result.point.y,
                        z: result.point.z,
                    },
                    color: "#FF0000",
                },
                1
            );
        }
    }, []);

    useEffect(() => {
        if (coordinatesMode) {
            document
                .getElementsByClassName("adsk-viewing-viewer")[0]
                ?.addEventListener("click", handleCoordinatesClick);
        } else {
            document
                .getElementsByClassName("adsk-viewing-viewer")[0]
                ?.removeEventListener("click", handleCoordinatesClick);
        }
    }, [coordinatesMode, handleCoordinatesClick]);

    useEffect(() => {
        UserPermitsCall();
    }, [project, userId]);

    usePureEffect(() => {
        userInformationService.setUserInformation({}).then((resp) => {
            setUserProfileId(resp.data.userId);
            setUserProfilePhoto(resp.data.photo);
        });

        alarmsService.getUsersFromAzure({ projectId: project.id }).then((res) => {
            setUsersFromAzure(res.data);
            setUsersWithAccessCount(res.data.length);
        });

        if (userPermitsConnection.state === HubConnectionState.Connected) {
            userPermitsConnection.stop().then(() => {
                subscribeToUserPermitsSignalR();
            });
        } else {
            subscribeToUserPermitsSignalR();
        }

        if (providerConnection.state === HubConnectionState.Connected) {
            providerConnection.stop().then(() => {
                subscribeToProviderSignalR();
            });
        } else {
            subscribeToProviderSignalR();
        }

        authService.getProfile().then((resp) => {
            if (resp) {
                setUserId(resp.oid);
                setUserName(resp.name as string);
            }
        });
    }, [project]);

    useEffect(() => {
        authService.getUser().then((user) => {
            const validEncoded = user?.access_token.split(".")[1];
            const decondedAccessToken = validEncoded ? atob(validEncoded) : "yay";

            const convertedAccessToken = JSON.parse(decondedAccessToken);

            setUserRoles(convertedAccessToken.roles);
        });
    }, []);

    return (
        <PanelProjectContext.Provider
            value={{
                project,
                setProject,
                notifications,
                setNotifications,
                notificationsCount,
                setNotificationsCount,
                alarmsCount,
                setAlarmsCount,
                redirectCommentsPanel,
                setRedirectCommentsPanel,
                isViewerReady,
                setisViewerReady,
                view,
                setView,
                setDbidsToRender,
                dbidsToRender,
                hoveredDeviceInfo,
                setHoveredDeviceInfo,
                rightPanelsData,
                setRightPanelsData,
                isRightPanelOpen,
                setIsRightPanelOpen,
                isViewerLoading,
                setIsViewerLoading,
                isRenderingItems,
                setIsRenderingItems,
                assetSelection,
                setAssetSelection,
                viewerMode,
                setViewerMode,
                dbidsAlarmsHeatMap,
                setDbidsAlarmsHeatMap,
                renderModel,
                setRenderModel,
                userProfile,
                setUserProfile,
                userRole,
                setUserRole,
                userPermits,
                setUserPermits,
                cellValues,
                workOrdersOpen,
                setWorkOrdersOpen,
                setCellValues,
                editing,
                setEditing,
                changePermitsTables,
                setChangePermitsTables,
                submitEditChanges,
                setSubmitEditChanges,
                users,
                setUsers,
                cellValuesRoles,
                setCellValuesRoles,
                roles,
                setRoles,
                tableFrom,
                setTableFrom,
                showSave,
                setShowSave,
                submitEditChangesRoles,
                setSubmitEditChangesRoles,
                assetType,
                setAssetType,
                assetEditModel,
                setAssetEditModel,
                parent,
                setParent,
                dbIdsToEdit,
                setDbIdsToEdit,
                assetIdEdit,
                setAssetIdEdit,
                openEditAsset,
                setOpenEditAsset,
                assetsWithSameDbid,
                setAssetsWithSameDbid,
                userId,
                setUserId,
                filterView,
                setFilterView,
                renderModelTypes,
                setRenderModelTypes,
                userProfileId,
                userProfilePhoto,
                powerBIFilteredData,
                setPowerBIFilteredData,
                powerBILegend,
                setPowerBILegend,
                powerBIFilter,
                setPowerBIFilter,
                openDialogEditUser,
                setOpenDialogEditUser,
                openDialogRole,
                setOpenDialogRole,
                projectsCount,
                setProjectsCount,
                documentsCount,
                setDocumentsCount,
                teamsCount,
                setTeamsCount,
                workOrdersCount,
                setWorkOrdersCount,
                usersWithAccessCount,
                setUsersWithAccessCount,
                powerBIReportsCount,
                setPowerBIReportsCount,
                chatSubscriptionsCount,
                setChatSubscriptionsCount,
                signalSubscriptionsCount,
                setSignalSubscriptionsCount,
                rightPanelButtonsLoading,
                setRightPanelButtonsLoading,
                interferenceOrdersOpen,
                setInterferenceOrdersOpen,
                isUserAllowedById,
                createRouteBasedOnAuth,
                isUserPermitsLoading,
                errorInUserPemirts,
                openDialogUsersAssociates,
                setOpenDialogUsersAssociates,
                showDialogEditRole,
                setShowDialogEditRole,
                showDeleteDialog,
                setShowDeleteDialog,
                foreingCellsValues,
                setForeingCellsValues,
                foreingHeaderValues,
                setForeingHeaderValues,
                clickCoords,
                coordinatesMode,
                setCoordinatesMode,
                floorPlanes,
                setFloorPlanes,
                isolatedAssets,
                setIsolatedAssets,
                areaVisualization,
                setAreaVisualization,
                usersFromAzure,
                setUsersFromAzure,
                validateLevelAccess,
                heatmapLegend,
                setHeatmapLegend,
                heatmapList,
                setHeatmapList,
                heatmapSelected,
                setHeatmapSelected,
                heatmap,
                setHeatmap,
                selectedSensorId,
                setSelectedSensorId,
                isHeatmapListLoading,
                setIsHeatmapListLoading,
                isHeatmapLoading,
                setIsHeatmapLoading,
                createHeatmap,
                showHMTooltips,
                setShowHMTooltips,
                isolateHMAssets,
                setIsolateHMAssets,
                heatmapSignalRState,
                setHeatmapSignalRState,
                userRoles,
                setUserRoles,
                cleanHeatmapData,
                requestedDatetime,
                setRequestedDatetime,
                redirectToChatThread,
                setRedirectToChatThread,
                documentsTag,
                setDocumentsTag,
                messages,
                setMessages,
                imageCache,
                setImageCache,
                userName,
                setUserName,
            }}
        >
            {children}
        </PanelProjectContext.Provider>
    );
};

export { PanelProjectProvider };

export const usePanelProjectContext = (): IPanelProjectContext => {
    const panelProjectContext = useContext(PanelProjectContext);
    if (panelProjectContext === undefined) {
        throw new Error("usepanelProjectContext must be inside a panelProjectContext");
    }
    return panelProjectContext;
};
